import { z } from 'zod';
import { DebtBreakdownSchema } from './relay';
/**
 * Deal state machine (Phase 13 — SCAFFOLD ONLY).
 *
 * A "deal" is the post-win lifecycle of a single won car: from the upstream
 * win through invoicing, deposit application, payment, export docs, transit,
 * customs, and delivery to close. It picks up where the relay desk leaves off
 * (`relayStatus: 'won'` → deal `won`) and connects to the debt side via the
 * relay `computeClientDebt` / Phase-8 landed-cost breakdown (see
 * {@link DealInvoiceSchema}).
 *
 * ⛔ No money moves in Phase 13 — see `docs/adr/ADR-payments-custody.md`. The
 * `deposit_applied` and `paid` transitions are pure STATE flips here; wiring
 * them to real settlement is USER-gated.
 *
 * Transitions are FORWARD-ONLY along the ladder, with a single escape hatch:
 * an admin override (with a mandatory reason) may move a deal to any state.
 *
 * Two states sit OFF the ladder: `voided`, what a deal becomes when the win it
 * was created from is re-awarded to somebody else, and `forfeited`, what it
 * becomes when the client refuses to pay for the car and the operator withholds
 * the deposit (owner decision (a) — see {@link DealStateSchema}).
 */
/**
 * Deal states: the nine ladder states in forward order (`closed` is terminal),
 * plus the one OFF-LADDER terminal state.
 *
 * `voided` is not a rung — nothing advances into it. It is what a deal becomes
 * when the win it was created from is taken away: re-awarding a lot with
 * `forceReplace` demotes the previous winner's bid to 'Not Won' and deletes its
 * `won_cars` projection, and its deal must stop being a live deal in the same
 * transaction. It is a STATE rather than a delete so the audit trail (and the
 * deal's own `history`) survives the correction.
 */
export const DealStateSchema = z.enum([
    'won',
    'invoice_issued',
    'deposit_applied',
    'paid',
    'export_docs',
    'in_transit',
    'customs',
    'delivered',
    'closed',
    'voided',
    /**
     * OFF-LADDER terminal (owner decision (a), 2026-09-28): the client refused to
     * pay for the won car, the operator confirmed it and the WHOLE deposit was
     * withheld as the penalty (`POST /api/admin/deals/:dealId/forfeit-deposit`,
     * `deal-forfeit.ts`). Like `voided` it is a STATE rather than a delete, so
     * the deal's `history` and its audit events survive; unlike `voided` the win
     * itself is not taken away — the bid still reads `Won`. Appended after
     * `voided` so every existing value keeps its position.
     */
    'forfeited',
]);
export const DEAL_STATES = DealStateSchema.options;
/**
 * The states that sit OFF the forward ladder: nothing advances into them and
 * nothing advances out of them. Reached only by a dedicated write (the win
 * correction for `voided`, the admin-confirmed refusal for `forfeited`), each
 * recorded as an override that carries its reason.
 */
export const DEAL_OFF_LADDER_STATES = ['voided', 'forfeited'];
/** The forward ladder only — the off-ladder states are reachable beside it, never along it. */
export const DEAL_LADDER_STATES = DEAL_STATES.filter((state) => !DEAL_OFF_LADDER_STATES.includes(state));
/**
 * Phase 13 — how long a deal may sit in each NON-terminal rung before the
 * platform raises `deal.overdue` to staff (DealSlaService, hourly, one alert
 * per deal per UTC day). `closed`, `voided` and `forfeited` have no SLA:
 * nothing is waiting on anybody. Hours, not days, because the first rung is a working day.
 *
 * A single table in the contract rather than a `pricing_rules` override: the
 * numbers describe the operator's promise to the client, not a tariff, and a
 * second source of them would only let the two disagree.
 */
export const DEAL_SLA_HOURS = {
    won: 24,
    invoice_issued: 120,
    deposit_applied: 72,
    paid: 120,
    export_docs: 168,
    in_transit: 336,
    customs: 168,
    delivered: 72,
};
/** The SLA for a state, or null for the terminal states (`closed`, `voided`, `forfeited`). */
export function dealSlaHoursFor(state) {
    return Object.prototype.hasOwnProperty.call(DEAL_SLA_HOURS, state)
        ? DEAL_SLA_HOURS[state]
        : null;
}
/**
 * True when a deal that entered `state` at `stateEnteredAt` has exceeded its
 * SLA at `now`. Terminal states are never overdue.
 */
export function isDealOverdue(state, stateEnteredAt, now) {
    const hours = dealSlaHoursFor(state);
    if (hours === null)
        return false;
    return now.getTime() - stateEnteredAt.getTime() > hours * 60 * 60 * 1000;
}
/**
 * `deal.overdue` outbox event (Phase 13 DealSlaService). Staff-facing: the
 * client is not told their own deal is late — that is the operator's problem
 * to fix, not the client's to worry about. `userId` is carried so the admin
 * template can name (masked) whose deal it is.
 */
export const DealOverdueEventSchema = z.object({
    type: z.literal('deal.overdue'),
    data: z.object({
        dealId: z.string(),
        userId: z.string().nullish(),
        wonCarId: z.string().nullish(),
        carId: z.string().nullish(),
        bidId: z.string().nullish(),
        state: DealStateSchema,
        /** ISO timestamp the deal entered `state` (the clock the SLA reads). */
        stateEnteredAt: z.string(),
        slaHours: z.number(),
        /** Whole hours past the SLA at sweep time. */
        overdueHours: z.number(),
        /** ISO timestamp of the sweep. */
        when: z.string(),
    }),
});
/**
 * Forward-only transition matrix. Key = current state, value = the states it
 * may advance to WITHOUT an admin override. Each non-terminal state advances
 * to exactly its successor; `closed` is terminal (no forward transitions).
 *
 * NOTE: the ladder is strictly sequential by design (no skipping) — an admin
 * override is the ONLY way to jump or move backward, and it must carry a
 * reason (see {@link canTransition}).
 */
export const DEAL_TRANSITIONS = {
    won: ['invoice_issued'],
    invoice_issued: ['deposit_applied'],
    deposit_applied: ['paid'],
    paid: ['export_docs'],
    export_docs: ['in_transit'],
    in_transit: ['customs'],
    customs: ['delivered'],
    delivered: ['closed'],
    closed: [],
    // Off-ladder terminal. NOTE the consequence, which is deliberate: because no
    // state lists `voided` as a successor, `canTransition(x, 'voided')` is only
    // ever true WITH an admin override + reason — a deal cannot be voided by
    // walking the ladder. MarkWonService writes the state directly inside the
    // win transaction (it is a correction of the win, not a ladder move), and an
    // admin can undo it only with an override that records why.
    voided: [],
    // Off-ladder terminal, same consequence as `voided`: only the dedicated
    // forfeit write (an admin-confirmed refusal with a reason) ever lands here.
    forfeited: [],
};
/**
 * Pure transition guard.
 *
 * - Normal path: `to` must be the immediate forward successor of `from`.
 * - Admin override: any `from → to` (including backward / skip) is allowed,
 *   BUT a non-empty `reason` is mandatory — an override without a reason is
 *   rejected (`OVERRIDE_REASON_REQUIRED`), never silently allowed.
 * - A no-op (`from === to`) is always rejected (`SAME_STATE`).
 */
export function canTransition(from, to, options = {}) {
    const fromAllowed = DEAL_TRANSITIONS[from];
    const toAllowed = DEAL_TRANSITIONS[to];
    if (fromAllowed === undefined || toAllowed === undefined) {
        return { ok: false, code: 'UNKNOWN_STATE' };
    }
    if (from === to) {
        return { ok: false, code: 'SAME_STATE' };
    }
    if (options.adminOverride) {
        if (!options.reason || options.reason.trim() === '') {
            return { ok: false, code: 'OVERRIDE_REASON_REQUIRED' };
        }
        return { ok: true };
    }
    if (fromAllowed.length === 0) {
        return { ok: false, code: 'TERMINAL' };
    }
    if (!fromAllowed.includes(to)) {
        return { ok: false, code: 'FORWARD_ONLY' };
    }
    return { ok: true };
}
/**
 * Invoice built from the client-debt / landed-cost breakdown when a deal is
 * invoiced. `lines` mirror the relay `DebtBreakdown` items (bid + auction fee +
 * service fee); totals stay per-currency until the Phase 8/14 tariff engine
 * owns FX. Scaffold: this is a data shape, not a billing document.
 */
export const DealInvoiceSchema = z.object({
    dealId: z.string(),
    breakdown: DebtBreakdownSchema,
    issuedAt: z.coerce.date(),
});
/**
 * `deal.state_changed` outbox event — appended in the SAME transaction as the
 * deal-state flip (same transactional-outbox guarantee as the relay desk).
 */
export const DealStateChangedEventSchema = z.object({
    type: z.literal('deal.state_changed'),
    data: z.object({
        dealId: z.string(),
        wonCarId: z.string().nullish(),
        userId: z.string().nullish(),
        /** The deal's lot (`alpicar_cars._id`) — what notifications name the car by. */
        carId: z.string().nullish(),
        from: DealStateSchema,
        to: DealStateSchema,
        /** Admin who performed the transition. */
        who: z.string(),
        when: z.string(), // ISO timestamp
        /** True when this was an admin override off the forward ladder. */
        override: z.boolean(),
        /** Mandatory when `override` is true. */
        reason: z.string().nullish(),
    }),
});
/**
 * One rung of a deal's own audit ladder, appended on every state entry. The
 * `deals` collection is `strict:false`, so this array is the only place the
 * post-win history is recorded — the outbox events are fanout, not storage.
 */
export const DealHistoryEntrySchema = z.object({
    state: DealStateSchema,
    at: z.coerce.date(),
    /** Actor who caused the entry (admin userId); null for platform-driven ones. */
    by: z.string().nullish(),
    /**
     * Why an OFF-LADDER entry happened, in the operator's words — written on the
     * `forfeited` rung (owner decision (a)). Absent on every ladder move.
     */
    reason: z.string().nullish(),
});
/**
 * A `deals` document as the single MarkWon use case writes it (Phase 5).
 *
 * Every field is optional because the collection is `strict:false` and the
 * Phase-13 scaffold shipped documents that predate the win-chain link
 * (`bidId`/`carId`/`wonAt`/`debtBreakdown`). Readers must therefore treat the
 * link fields as possibly absent; `effectiveDealState` in the api's
 * DealsService covers the same gap for `state`.
 *
 * `bidId` is the IDEMPOTENCY KEY of the whole post-win chain: one deal per won
 * bid, enforced by the sparse unique index `deals.deals_bid_unique` (see
 * apps/api/src/database/index-definitions.ts) and re-checked in
 * DealsService.createFromWonBid so a retried transaction cannot double-insert.
 */
export const DealSchema = z.object({
    state: DealStateSchema.optional(),
    /** When the deal entered `state` — the clock the SLA/ageing reports read. */
    stateEnteredAt: z.coerce.date().optional(),
    history: z.array(DealHistoryEntrySchema).optional(),
    /** The won bid this deal was created from — one deal per bid. */
    bidId: z.string().optional(),
    carId: z.string().optional(),
    /** `won_cars` document id, when the projection was written alongside. */
    wonCarId: z.string().nullish(),
    userId: z.string().optional(),
    wonAt: z.coerce.date().optional(),
    /** Client debt at win time (relay `computeClientDebt`). */
    debtBreakdown: DebtBreakdownSchema.optional(),
    invoice: DealInvoiceSchema.nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
});
//# sourceMappingURL=deal.js.map